import React, { useState, useEffect, useRef } from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space, Modal } from "antd";
import type { InputRef } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import axios from "axios";
import moment from "moment";
import "./data_wh_transfer_request.css";
import { useNavigate } from "react-router-dom";

interface DataType {
    key: string;
    transfer_request_no: string;
    transfer_request_date: string;
    request_by: string;
    warehouse_origin: string;
    warehouse_destination: string;
    status: number | string;
}

type DataIndex = keyof DataType;

const DataWHTransferRequest: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const navigate = useNavigate();
    const [data, setData] = useState<DataType[]>([]);
    const [loading, setLoading] = useState(false);
    const [tableParams, setTableParams] = useState<any>({ filters: {}, sorter: {} });
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const { filters, sorter } = tableParams;
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-requests`, {
                    params: {
                        sortField: sorter.field,
                        sortOrder: sorter.order,
                        ...filters
                    }
                });
                const formattedData = response.data.map((item: any) => ({
                    ...item,
                    key: item.transfer_request_no,
                }));
                setData(formattedData);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [tableParams]);

    
     const handleTableChange = (pagination: any, filters: any, sorter: any) => {
         setTableParams({ filters, sorter }); 
     };

    const handleRowDoubleClick = async (record: DataType) => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-request/${record.transfer_request_no}`);
            navigate('/dashboard/item_wh_transfer_request', { state: { requestData: response.data } });
        } catch (error) {
            console.error("Failed to fetch transfer request details:", error);
            Modal.error({
                title: 'Failed to Load Data',
                content: 'Could not retrieve the details for the selected transfer request.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.replace(/_/g, ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) => text,
    });

    const columns: ColumnsType<DataType> = [
        {
            title: 'Trans Request No',
            dataIndex: 'transfer_request_no',
            key: 'transfer_request_no',
            sorter: true, 
            ...getColumnSearchProps('transfer_request_no'),
        },
        {
            title: 'Request Date',
            dataIndex: 'transfer_request_date',
            key: 'transfer_request_date',
            sorter: true, 
            render: (text) => moment(text).format('DD/MM/YYYY HH:mm'),
            ...getColumnSearchProps('transfer_request_date'),
        },
        {
            title: 'Request By',
            dataIndex: 'request_by',
            key: 'request_by',
            sorter: (a, b) => (a.request_by || '').localeCompare(b.request_by || ''),
            ...getColumnSearchProps('request_by'),
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse_origin',
            key: 'warehouse_origin',
            sorter: (a, b) => a.warehouse_origin.localeCompare(b.warehouse_origin),
            ...getColumnSearchProps('warehouse_origin'),
        },
        {
            title: 'To Warehouse',
            dataIndex: 'warehouse_destination',
            key: 'warehouse_destination',
            sorter: (a, b) => a.warehouse_destination.localeCompare(b.warehouse_destination),
            ...getColumnSearchProps('warehouse_destination'),
        },
        {
            title: 'Order Status',
            dataIndex: 'status',
            key: 'status',
            sorter: true, 
            ...getColumnSearchProps('status'),
            render: (status: number | string) => {
                const statusValue = parseInt(String(status), 10);
                switch (statusValue) {
                    case 1:
                        return 'Draft';
                    case 2:
                        return 'Accept';
                    case 3:
                        return 'Ready';
                    case 4:
                        return 'Reject';
                    case 5:
                        return 'Closed';
                    case 6:
                        return 'Intransit';
                    default:
                        return status;
                }
            },
        },
    ];

    return (

        <div id="ieml" style={transformStyle}>
            <div className="gjs-grid-row" id="imp2">
                <div className="gjs-grid-column" id="ivq7">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data WH Transfer Request</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/item_wh_transfer_request",
                                title: "Item WH Transfer Request",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="i0p4e">
                <div className="gjs-grid-column" id="ipsog">
                    <Table
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        rowKey="key"
                        scroll={{ x: 1300 }}
                        onChange={handleTableChange} 
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    handleRowDoubleClick(record);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataWHTransferRequest;